import { useCallback, useEffect, useMemo, useReducer } from "react";
// utils
import { authApi } from "src/api/auth";
import { STORAGE_KEY, setSession } from "src/utils/axios";
//
import { AuthContext } from "./auth-context";
import { ActionMapType, AuthAccountType, AuthStateType } from "src/auth/types";
import { ROLE_NUMBER_TO_STRING } from "src/commons/const/accounts";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = "INITIAL",
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
  LOGOUT = "LOGOUT",
  REFETCH = "REFETCH",
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthAccountType;
  };
  [Types.LOGIN]: {
    user: AuthAccountType;
  };
  [Types.REGISTER]: {
    user: AuthAccountType;
  };
  [Types.LOGOUT]: undefined;
  [Types.REFETCH]: {
    user: AuthAccountType;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  if (action.type === Types.REFETCH) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);

      if (accessToken) {
        setSession(accessToken);

        // セッションストレージからユーザー情報を復元
        const savedUser = sessionStorage.getItem("currentUser");
        const user = savedUser ? JSON.parse(savedUser) : null;

        if (user) {
          dispatch({
            type: Types.INITIAL,
            payload: {
              user,
            },
          });
        } else {
          setSession(null);
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      setSession(null);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (id: number, password: string): Promise<void> => {
      try {
        const response = await authApi.login({ id, password });

        if (response.access_token) {
          setSession(response.access_token);

          const adminUser: AuthAccountType = {
            id: response.user.id.toString(),
            name: response.user.name,
            role:
              ROLE_NUMBER_TO_STRING[
                response.user
                  .role as unknown as keyof typeof ROLE_NUMBER_TO_STRING
              ] || "service_provider",
            created_at: response.user.created_at,
            updated_at: response.user.updated_at,
          };

          // ユーザー情報をセッションストレージに保存
          sessionStorage.setItem("currentUser", JSON.stringify(adminUser));

          dispatch({
            type: Types.LOGIN,
            payload: {
              user: adminUser,
            },
          });
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "ログインできませんでした。";

        throw new Error(errorMessage);
      }
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    sessionStorage.removeItem("currentUser");
    setSession(null);
    dispatch({
      type: Types.LOGOUT,
    });
  }, []);

  // REFETCH
  const reFetch = useCallback(async () => {
    const savedUser = sessionStorage.getItem("currentUser");
    const user = savedUser ? JSON.parse(savedUser) : null;

    dispatch({
      type: Types.REFETCH,
      payload: {
        user,
      },
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: "jwt",
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
      login,
      logout,
      reFetch,
    }),
    [login, logout, reFetch, state.user, status]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
